import React, { useState, useContext, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { NotificationContext } from '@/contexts/NotificationContext';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '@/contexts/NavigationContext';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { downloadContactExportFromNotification } from '@/lib/notifications/contactExportDownload';
import { InAppNotificationDialog } from './InAppNotificationDialog';
import { NotificationRow } from './NotificationRow';
import {
  InAppAnnouncement,
  inAppAnnouncementOf,
  isForCurrentSurface,
} from '@/lib/notifications/inAppNotification';

const ERASE_DURATION_MS = 420;
const ERASE_STAGGER_MS = 60;
const ERASE_MAX_TOTAL_MS = 900;
const HOVER_CLOSE_DELAY_MS = 180;

export const NotificationDropdown: React.FC<{
  triggerIcon?: React.ReactNode;
  triggerClassName?: string;
  badgeStyle?: 'count' | 'dot';
}> = ({ triggerIcon, triggerClassName, badgeStyle = 'count' }) => {

  const notificationContext = useContext(NotificationContext);
  const notifications = notificationContext?.notifications || [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const isLoading = notificationContext?.isLoading || false;
  const markAsRead = notificationContext?.markAsRead || (async () => { });
  const markAllAsRead = notificationContext?.markAllAsRead || (async () => { });

  const [isOpen, setIsOpen] = useState(false);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openedByHover = useRef(false);
  const lastPointerType = useRef<string | null>(null);
  const cancelHoverClose = () => {
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  };
  const openOnHover = () => {
    cancelHoverClose();
    if (!isOpen) {
      openedByHover.current = true;
      setIsOpen(true);
    }
  };
  const scheduleHoverClose = () => {
    cancelHoverClose();
    hoverCloseTimer.current = setTimeout(() => setIsOpen(false), HOVER_CLOSE_DELAY_MS);
  };
  useEffect(() => cancelHoverClose, []);
  const isMobile = useIsMobile();
  const [isClearing, setIsClearing] = useState(false);
  const [openedAnnouncement, setOpenedAnnouncement] = useState<{
    title: string;
    createdAt: string;
    announcement: InAppAnnouncement;
  } | null>(null);
  const navigate = useNavigate();
  const { interceptedNavigate } = useNavigation();
  const { selectedAccountId, setSelectedAccountId } = useBusinessAccountContext();
  const { selectedWorkspaceId, setSelectedWorkspaceId } = useWorkspaceContext();

  if (!notificationContext) {
    return (
      <div className="flex-shrink-0">
        {triggerIcon || <Bell className="w-4 h-4 sm:w-5 sm:h-5" />}
      </div>
    );
  }

  const handleMarkAllAsRead = async () => {
    if (isClearing) return; // double-click would restart the animation mid-flight
    const rowCount = recentNotifications.length;
    setIsClearing(true);
    if (rowCount > 0) {
      const total = Math.min(
        ERASE_DURATION_MS + ERASE_STAGGER_MS * Math.max(rowCount - 1, 0),
        ERASE_MAX_TOTAL_MS,
      );
      await new Promise((resolve) => setTimeout(resolve, total));
    }
    try {
      await markAllAsRead();
    } finally {
      setIsClearing(false);
    }
  };

  const handleNotificationClick = async (notification: any) => {
    setIsOpen(false); // Close dropdown

    const data = notification.data || {};

    const notificationWorkspaceId = notification.workspaceId || notification.workspace_id;
    const notificationAccountId = notification.businessAccountId || notification.business_account_id;

    if (notificationWorkspaceId && notificationWorkspaceId !== selectedWorkspaceId) {
      setSelectedWorkspaceId(notificationWorkspaceId);
    }
    if (notificationAccountId && notificationAccountId !== selectedAccountId) {
      setSelectedAccountId(notificationAccountId);
    }

    const announcement = inAppAnnouncementOf(notification);
    if (announcement) {
      setOpenedAnnouncement({
        title: notification.title,
        createdAt: notification.createdAt,
        announcement,
      });
      return;
    }

    if (data?.kind === 'board_auto_send_failure' && typeof data.deep_link === 'string') {
      interceptedNavigate(data.deep_link);
      return;
    }

    if (
      data?.kind === 'contact_export_result' &&
      notification.type === 'CONTACT_EXPORT_COMPLETED'
    ) {
      const downloaded = await downloadContactExportFromNotification(data);
      if (downloaded) return;
    }

    if (
      typeof data?.deep_link === 'string' &&
      data.deep_link.startsWith('/') &&
      !data.deep_link.startsWith('//') // '/x' only — '//host' is protocol-relative, i.e. external
    ) {
      const agentId = data.agent_id || data.agentId;
      const userIp = data.user_ip || data.userIp || data.visitor_ip || data.visitorIp;
      interceptedNavigate(data.deep_link, {
        state: {
          agentId,
          selectedAiIp: userIp,
        },
      });
      return;
    }

    switch (notification.type) {
      case 'AI_AGENT_NEEDS_ATTENTION': {
        const agentId = data.agent_id || data.agentId;
        const userIp = data.user_ip || data.userIp || data.visitor_ip || data.visitorIp;
        let url = '/home/channels/ai-agents';
        if (agentId) {
          url += `?agentId=${encodeURIComponent(agentId)}`;
          if (userIp) {
            url += `&userIp=${encodeURIComponent(userIp)}`;
          }
        }
        interceptedNavigate(url, {
          state: {
            agentId,
            selectedAiIp: userIp,
          },
        });
        break;
      }
      case 'CAMPAIGN_CREATED':
      case 'CAMPAIGN_COMPLETED':
      case 'CAMPAIGN_COMPLETED_WITH_ERRORS':
      case 'CAMPAIGN_SCHEDULED':
      case 'CAMPAIGN_STARTED':
      case 'CAMPAIGN_RETRY_FAILED':
        if (data.campaignId) {
          interceptedNavigate(`/home/campaigns?campaignId=${encodeURIComponent(data.campaignId)}`);
        } else {
          interceptedNavigate('/home/campaigns');
        }
        break;
      case 'MESSAGE_RECEIVED':
      case 'chat_request':
      case 'customer_interaction':
      case 'cake_order_form':
      case 'cake_interest':
      case 'treats_interest':
      case 'order_confirmed':
      case 'session_ended': {
        const conversationId = data.conversationId || data.conversation_id;
        if (conversationId) {
          interceptedNavigate(`/home/inbox?conversationId=${conversationId}`, {
            state: {
              selectedConversationId: conversationId,
              contactName: data.contactName || data.contact_name,
              targetMessageId: data.messageId || data.message_id || null,
            },
          });
        } else {
          const messageMatch = notification.message?.match(/^New message from (.+?):/);
          const contactName = data.contactName || data.contact_name || (messageMatch ? messageMatch[1] : null);
          interceptedNavigate('/home/inbox', {
            state: {
              searchContactName: contactName,
            },
          });
        }
        break;
      }
      case 'TEMPLATE_APPROVED':
      case 'TEMPLATE_REJECTED':
        interceptedNavigate('/home/templates');
        break;
      case 'BUSINESS_ACCOUNT_STATUS_CHANGE':
        interceptedNavigate('/home/settings');
        break;
      case 'TEAM_INVITATION':
        interceptedNavigate('/home/team-management');
        break;
      case 'TICKET_ASSIGNED':
      case 'TICKET_COMMENT': {
        const ticketId = data.ticketId || data.ticket_id;
        if (ticketId) {
          interceptedNavigate(`/home/tickets?ticketId=${encodeURIComponent(ticketId)}`);
        } else {
          interceptedNavigate('/home/tickets');
        }
        break;
      }
      case 'CONVERSATION_ASSIGNED': {
        const convId = data.conversationId || data.conversation_id;
        if (convId) {
          interceptedNavigate(`/home/inbox?conversationId=${convId}`, {
            state: {
              selectedConversationId: convId,
              contactName: data.contactName || data.contact_name || null,
            },
          });
        } else {
          interceptedNavigate('/home/inbox');
        }
        break;
      }
      case 'SYSTEM_NOTIFICATION':
      case 'SYSTEM_ALERT':
      case 'SYSTEM_INFO':
        interceptedNavigate('/home/notifications');
        break;
      case 'REMINDER': {
        const conversationId = data.conversationId || data.conversation_id;
        const contactId = data.contactId || data.contact_id;
        if (conversationId) {
          interceptedNavigate(`/home/inbox?conversationId=${conversationId}`, {
            state: { selectedConversationId: conversationId },
          });
        } else if (contactId) {
          interceptedNavigate(
            `/home/contacts?contactId=${contactId}&tab=reminders`
          );
        } else {
          interceptedNavigate('/home/settings?tab=reminders');
        }
        break;
      }
      default:
        interceptedNavigate('/home/notifications');
        break;
    }
  };

  const seenIds = new Set<string>();
  const recentNotifications = [...notifications]
    .filter(n => !n.isRead)
    .filter(n => {
      const announcement = inAppAnnouncementOf(n);
      return !announcement || isForCurrentSurface(announcement);
    })
    .filter(n => {
      if (seenIds.has(n.id)) return false;
      seenIds.add(n.id);
      return true;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 10);

  return (
    <>
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onMouseEnter={openOnHover}
          onMouseLeave={scheduleHoverClose}
          onPointerDown={(e) => {
            lastPointerType.current = e.pointerType;
          }}
          onClick={(e) => {
            if (lastPointerType.current === 'mouse' && isOpen) e.preventDefault();
            lastPointerType.current = null;
          }}
          className={cn(
            'relative cursor-pointer',
            triggerClassName ?? 'p-[8px] w-[40px] h-[40px] rounded-[47px] bg-[#F5F7F9]'
          )}
        >
          {triggerIcon ? triggerIcon : <Bell className="h-5 w-5" />}
          {unreadCount > 0 &&
            (badgeStyle === 'dot' ? (
              <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-brand ring-2 ring-card" />
            ) : (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px]"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            ))}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={isMobile ? 16 : 0}
        onMouseEnter={openOnHover}
        onMouseLeave={scheduleHoverClose}
        onOpenAutoFocus={(e) => {
          if (openedByHover.current) e.preventDefault();
        }}
        onCloseAutoFocus={(e) => {
          if (openedByHover.current) e.preventDefault();
          openedByHover.current = false;
        }}
        className="w-[calc(100vw-32px)] sm:w-[min(380px,calc(100vw-16px))] rounded-none border-0 bg-transparent p-0 shadow-none"
      >
        <div className="flex max-h-[min(520px,calc(100vh-100px))] flex-col overflow-hidden rounded-2xl border border-dash-border bg-card shadow-overlay">
        <div className="flex shrink-0 items-center justify-between gap-3 px-4 pb-3 pt-4">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="text-wn-base font-wn-semibold text-text-strongest">Notifications</h2>
            {unreadCount > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-pos-bg px-1.5 text-wn-xs font-wn-semibold tabular-nums text-pos-text">
                {unreadCount} new
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleMarkAllAsRead}
            disabled={unreadCount === 0 || isClearing}
            className="shrink-0 text-wn-sm font-wn-medium text-text-muted transition-colors hover:text-text-strongest disabled:opacity-40 disabled:hover:text-text-muted"
          >
            Mark all as read
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto border-t border-muted scrollbar-thin">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-brand" />
            </div>
          ) : recentNotifications.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="text-wn-md font-wn-semibold text-text-strongest">All caught up</p>
              <p className="mt-1 text-wn-sm text-text-muted">No unread notifications right now.</p>
            </div>
          ) : (
            <div className="divide-y divide-muted">
              {recentNotifications.map((notification, index) => (
                <div
                  key={notification.id}
                  className={cn(isClearing && 'animate-notification-erase')}
                  style={
                    isClearing
                      ? { animationDelay: `${index * ERASE_STAGGER_MS}ms` }
                      : undefined
                  }
                >
                  <NotificationRow
                    dense
                    notification={notification}
                    onClick={() => {
                      if (!notification.isRead) {
                        markAsRead(notification.id);
                      }
                      handleNotificationClick(notification);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-muted p-3">
          <button
            type="button"
            onClick={() => {
              interceptedNavigate('/home/notifications');
              setIsOpen(false);
            }}
            className="h-10 w-full rounded-xl bg-brand-subtle text-wn-md font-wn-semibold text-brand-cta transition-colors hover:bg-brand-soft"
          >
            View all notifications
          </button>
        </div>
        </div>
      </PopoverContent>
    </Popover>

    <InAppNotificationDialog
      open={!!openedAnnouncement}
      onClose={() => setOpenedAnnouncement(null)}
      title={openedAnnouncement?.title || ''}
      createdAt={openedAnnouncement?.createdAt}
      announcement={openedAnnouncement?.announcement ?? null}
    />
    </>
  );
};
